import { FORM_ERROR } from 'final-form';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';
import { Crypto, CryptoType } from '@vk-hr-tek/core/crypto';
import { showCustomNotification } from '@vk-hr-tek/core/notifications';
import {
  setDownloadWaiting,
  setDownloadComplete,
  setDownloadFailed,
} from '@vk-hr-tek/core/download-notification';
import { DownloadService } from '@vk-hr-tek/core/download';

import { ThunkExtra } from '@app/store';
import { UsersService } from '@app/gen/attorneys';

export const signAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      certificateId: string;
      companyName: string;
      kind: 'attorney' | 'revocation';
      cryptoType?: CryptoType;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/signAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);
      const crypto = inject(Crypto);

      if (!values.attorneys.length) {
        throw new Error('Список подписываемых документов пуст!');
      }

      const attorneyIds = values.attorneys.map(({ id }) => id);

      const [{ hashes }, certificate] = await Promise.all([
        service.getAttorneyXMLHashes({
          body: { attorney_ids: attorneyIds },
        }),
        crypto.getSertificateById(values.certificateId, values.cryptoType),
      ]);

      const signedAttorneys = await Promise.all(
        hashes.map(async (hash) => {
          return {
            id: hash.attorney_id,
            signature: await crypto.signHash(
              certificate,
              values.kind === 'attorney'
                ? hash.attorney_hash
                : hash.revocation_hash || '',
            ),
          };
        }),
      );

      try {
        await service.attorneyUkepSign({
          body: {
            attorneys: signedAttorneys.map(({ id, signature }) => ({
              attorney_id: id,
              signature,
              entity: values.kind,
            })),
          },
        });
      } catch (error) {
        throw new Error('Ошибка отправки подписи');
      }

      actions.resolve();

      const notification = {
        type: 'info' as const,
        title: '',
        message: '',
        subtitle: values.companyName,
      };

      if (values.attorneys.length > 1) {
        notification.title =
          values.kind === 'attorney' ? 'Подписаны МЧД!' : 'Подписаны отзывы!';
        notification.message =
          values.kind === 'attorney'
            ? `МЧД (${values.attorneys.length} шт)`
            : `Отзывы (${values.attorneys.length} шт)`;
      } else {
        notification.title =
          values.kind === 'attorney' ? 'Подписана МЧД!' : 'Подписан отзыв!';
        notification.message = values.attorneys[0].name;
      }

      dispatch(showCustomNotification([notification]));
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      const notification = {
        type: 'error' as const,
        title: '',
        message: '',
        subtitle: values.companyName,
      };

      if (values.attorneys.length > 1 || !values.attorneys.length) {
        notification.title =
          values.kind === 'attorney'
            ? 'Не удалось подписать МЧД!'
            : 'Не удалось подписать отзывы!';
        notification.message =
          values.kind === 'attorney'
            ? `МЧД (${values.attorneys.length} шт)`
            : `Отзывы (${values.attorneys.length} шт)`;
      } else {
        notification.title =
          values.kind === 'attorney'
            ? 'Не удалось подписать МЧД!'
            : 'Не удалось подписать отзыв!';
        notification.message = values.attorneys[0].name;
      }

      dispatch(showCustomNotification([notification]));
      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const deleteAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/deleteAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      if (!values.attorneys.length) {
        throw new Error('Список удаляемых МЧД пуст!');
      }

      await service.attorneysDelete({
        body: { attorney_ids: values.attorneys.map(({ id }) => id) },
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title:
              values.attorneys.length > 1
                ? 'Упешно удалены МЧД!'
                : 'Упешно удалена МЧД!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      dispatch(
        showCustomNotification([
          {
            type: 'error' as const,
            title: 'Не удалось удалить МЧД!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const revokeAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      reason: string;
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/revokeAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      if (!values.attorneys.length) {
        throw new Error('Список отзываемых МЧД пуст!');
      }

      await service.attorneysRevoke({
        body: {
          attorney_ids: values.attorneys.map(({ id }) => id),
          reason: values.reason,
        },
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title:
              values.attorneys.length > 1 ? 'Созданы отзывы!' : 'Создан отзыв!',
            message:
              values.attorneys.length > 1
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      dispatch(
        showCustomNotification([
          {
            type: 'error' as const,
            title:
              values.attorneys.length > 1
                ? 'Не удалось создать отзывы!'
                : 'Не удалось создать отзыв!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const unrevokeAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/unrevokeAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      if (!values.attorneys.length) {
        throw new Error('Список восстанавливаемых МЧД пуст!');
      }

      await service.attorneysUnrevoke({
        body: { attorney_ids: values.attorneys.map(({ id }) => id) },
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title:
              values.attorneys.length > 1
                ? 'Успешно удалены отзывы!'
                : 'Успешно удален отзыв!',
            message:
              values.attorneys.length > 1
                ? `Отзывы (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      dispatch(
        showCustomNotification([
          {
            type: 'error' as const,
            title:
              values.attorneys.length > 1
                ? 'Не удалось удалить отзывы!'
                : 'Не удалось удалить отзыв!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `Отзывы (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const cancelAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/cancelAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    try {
      const service = inject(UsersService);

      if (!values.attorneys.length) {
        throw new Error('Список отключаемых МЧД пуст!');
      }

      await service.attorneysRevokeImported({
        body: { attorney_ids: values.attorneys.map(({ id }) => id) },
      });

      actions.resolve();

      dispatch(
        showCustomNotification([
          {
            type: 'info' as const,
            title:
              values.attorneys.length > 1 ? 'Отключены МЧД!' : 'Отключена МЧД!',
            message:
              values.attorneys.length > 1
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );
    } catch (err) {
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      dispatch(
        showCustomNotification([
          {
            type: 'error' as const,
            title: 'Не удалось отключить МЧД!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);

export const exportAttorneys = createAsyncThunk<
  void,
  {
    values: {
      attorneys: {
        id: string;
        name: string;
      }[];
      companyName: string;
    };
    actions: {
      resolve: (value?: unknown) => void;
      reject: (value?: unknown) => void;
    };
  },
  ThunkExtra
>(
  'attorneys/exportAttorneys',
  async (
    { values, actions },
    { rejectWithValue, dispatch, extra: { inject } },
  ) => {
    const downloadId = String(Date.now());

    try {
      const service = inject(UsersService);

      if (!values.attorneys.length) {
        throw new Error('Список скачиваемых МЧД пуст!');
      }

      await dispatch(
        setDownloadWaiting({
          id: downloadId,
          count: values.attorneys.length,
        }),
      );

      const { file, filename } = await service.attorneyExport({
        query: {
          attorney_ids: values.attorneys.map(({ id }) => id).join(','),
        },
      });

      dispatch(setDownloadComplete({ id: downloadId }));

      await inject(DownloadService).doDownload(
        file,
        filename || 'documents.zip',
      );

      actions.resolve();
    } catch (err) {
      dispatch(setDownloadFailed({ id: downloadId }));
      const appError = new AppError('client', {
        code: 500,
        message: 'Упс! Что-то пошло не так.',
      });

      if (err instanceof Error && err.message) {
        appError.title = err.message;
      }

      actions.reject({ [FORM_ERROR]: appError });

      dispatch(
        showCustomNotification([
          {
            type: 'error' as const,
            title: 'Не удалось скачать МЧД!',
            message:
              values.attorneys.length > 1 || !values.attorneys.length
                ? `МЧД (${values.attorneys.length} шт)`
                : values.attorneys[0].name,
            subtitle: values.companyName,
          },
        ]),
      );

      return rejectWithValue(classToPlain(appError) as AppError);
    }
  },
);
